// @file Helper functions for dashboard.

import { galleryTextArray } from '@@/bits/collections_helper'
import type { UrlOptions } from '@@/bits/location'
import { getPathnameItems, getSearchParams, transformCurrentUrl } from '@@/bits/location'
import { safeLocalStorage } from '@@/bits/safe_storage'
import { LibraryType } from '@@/enums'
import type { WallViz } from '@@/types'
import type { WallsFilter } from '@@/types/collections'
import { CollectionKeyTypes } from '@@/types/collections'
import type { Audience, DashView } from '@@/types/dash'
import { ClassTabs, MobilePage } from '@@/types/dash'

function dashViewUrlParams(dashView: DashView): Record<string, string> {
  const { collectionKey, filter, gallery, mobilePage, page, tab } = dashView
  // The empty strings indicate that we want to remove those fields.
  const params: Record<string, string> = {
    page: page ?? '',
    mobile_page: mobilePage ?? '',
    filter: filter ?? '',
    gallery: gallery ?? '',
    tab: tab ?? '',
    folder_id: '',
    library_id: '',
  }

  if (collectionKey !== undefined && collectionKey !== null) {
    const { typeKey, indexKey } = collectionKey
    if (typeKey === CollectionKeyTypes.FolderId) {
      params.folder_id = String(indexKey)
    } else if (typeKey === CollectionKeyTypes.GroupFolderId) {
      params.group_id = String(indexKey)
    } else if (typeKey === CollectionKeyTypes.Filter) {
      params.filter = indexKey
    }
  }

  if (filter !== undefined) {
    params.filter = filter
  }

  if (gallery !== undefined) {
    params.gallery = gallery
  }

  if (tab !== undefined) {
    params.tab = tab
  }

  return params
}

function dashViewUrl(dashView: DashView, urlOptions?: UrlOptions): string {
  const searchParams = dashViewUrlParams(dashView)
  if (urlOptions?.searchParams != null) {
    Object.assign(searchParams, urlOptions.searchParams)
  }
  return transformCurrentUrl({ withQueryString: false }, { searchParams, path: urlOptions?.path ?? '/dashboard' })
}

const CrossLibraryToFilterMap = {
  recents: 'combined_recents',
  bookmarks: 'favorites',
  imports: 'imports',
  shared: 'combined_shared',
}

const TextToVizMap = {
  all: 'all',
  wall: 'grid',
  stream: 'stream',
  grid: 'matrix',
  shelf: 'shelf',
  map: 'map',
  canvas: 'free',
  timeline: 'timeline',
}

function getDashPathnameParams(): Record<string, string> {
  const pathnameItems = getPathnameItems()
  if (pathnameItems[0] !== 'dashboard') {
    return {}
  }

  const crossLibraryArray = ['bookmarks', 'recents', 'imports', 'shared']
  // path is /dashboard/:crossLibraryCollection
  if (crossLibraryArray.includes(pathnameItems[1])) {
    return { filter: CrossLibraryToFilterMap[pathnameItems[1]] as WallsFilter }
  }

  // path is /dashboard/gallery/:viz
  if (pathnameItems[1] === 'gallery') {
    if (galleryTextArray.includes(pathnameItems[2]) && pathnameItems[2] !== undefined) {
      return { gallery: TextToVizMap[pathnameItems[2]] }
    }

    // fallback to all if viz is incorrectly typed or not provided
    return { gallery: 'all' }
  }

  // path is /dashboard/make
  if (pathnameItems[1] === 'make') {
    return { make: 'make' }
  }

  // path is /dashboard/learn
  if (pathnameItems[1] === 'learn') {
    return { learn: 'learn' }
  }

  // path is /dashboard/<LibraryType>/:librarySlug(/:tab)
  if (Object.values(LibraryType).includes(pathnameItems[1] as LibraryType) && pathnameItems[2] !== undefined) {
    // path is /dashboard/school/:schoolSlug/class/:classSlugWithHashId
    if (pathnameItems[3] === 'class' && pathnameItems[4] !== undefined) {
      return { librarySlug: pathnameItems[2], classSlugWithHashId: pathnameItems[4] }
    }
    // path is /dashboard/school/:schoolSlug/discussion/:discussionHashId
    if (pathnameItems[3] === 'discussion' && pathnameItems[4] !== undefined) {
      return { librarySlug: pathnameItems[2], discussionHashId: pathnameItems[4] }
    }
    if (pathnameItems[3] === 'gallery') return { librarySlug: pathnameItems[2], gallery: 'all' }
    if (pathnameItems[3] === 'learn') return { librarySlug: pathnameItems[2], learn: 'learn' }
    if (pathnameItems[3] === 'make') return { librarySlug: pathnameItems[2], make: 'make' }
    return { librarySlug: pathnameItems[2] }
  }

  return {}
}

function getDashViewFromUrlParams(urlParams: Record<string, string> | null = null): DashView {
  const searchParams = urlParams ?? getSearchParams()
  const pathnameParams = getDashPathnameParams()
  const librarySlug = pathnameParams.librarySlug
  const classSlugWithHashId = pathnameParams.classSlugWithHashId
  const discussionHashId = (pathnameParams as Record<string, string | undefined>).discussionHashId
  const folderId = searchParams.folder_id
  let groupId = searchParams.group_id
  const filter = pathnameParams.filter ?? searchParams.filter
  const gallery = pathnameParams.gallery ?? searchParams.gallery
  const learn = pathnameParams.learn ?? searchParams.learn
  const make = pathnameParams.make ?? searchParams.make
  const tab = searchParams.tab
  const mobilePage = searchParams.mobile_page
  const page = searchParams.page
  const create = searchParams.create
  const audience = searchParams.audience

  // For campus class pages we extract the numeric group ID from the class hashid
  // since the group_id query param is not set in the URL for campus classes
  // For example, the class slug segment, "slug-HASHID", might look like: "ap-lang-9MmEePDA5DYQdBxk"
  const isCampusClass =
    librarySlug !== undefined && librarySlug !== '' && classSlugWithHashId !== undefined && classSlugWithHashId !== ''
  if (isCampusClass) {
    const hashid = classSlugWithHashId.split('-').pop()
    if (hashid) {
      const vueStartingState = (window as any).ww?.vueStartingState
      const classGroups = vueStartingState?.classGroups ?? []
      const matchingClass = classGroups.find((classGroup: any) => classGroup.hashid === hashid)
      if (matchingClass) {
        groupId = String(matchingClass.id)
      }
    }
  }

  const dashView: DashView = {}

  if (page !== undefined && page !== '') {
    dashView.page = page
  } else if (create !== undefined && create !== '') {
    dashView.page = 'layoutPicker'
  }

  if (mobilePage !== undefined && mobilePage !== '') {
    dashView.mobilePage = mobilePage as MobilePage
  }

  if (librarySlug !== undefined && librarySlug !== '') {
    dashView.librarySlug = librarySlug
    dashView.mobilePage = MobilePage.CollectionsMenu

    // If the librarySlug and filter is exist, the mobilePage should be collection
    if (filter !== undefined && filter !== '') {
      dashView.mobilePage = MobilePage.Collection
    }
  }

  if (classSlugWithHashId !== undefined && classSlugWithHashId !== '') {
    dashView.classSlugWithHashId = classSlugWithHashId
    dashView.mobilePage = MobilePage.Collection
  }

  // Map discussion path segment to discussionHashId on DashView
  if (discussionHashId !== undefined && discussionHashId !== '') {
    dashView.discussionHashId = discussionHashId
  }

  if (folderId !== undefined && folderId !== '') {
    dashView.collectionKey = { typeKey: CollectionKeyTypes.FolderId, indexKey: Number(folderId) }
  } else if (groupId !== undefined && groupId !== '') {
    dashView.collectionKey = { typeKey: CollectionKeyTypes.GroupFolderId, indexKey: Number(groupId) }
  } else if (filter !== undefined && filter !== '') {
    dashView.filter = filter as WallsFilter
  }

  if (gallery !== undefined && gallery !== '') {
    dashView.gallery = gallery as WallViz
  }

  if (audience !== undefined && audience !== '') {
    dashView.audience = audience as Audience
  }

  if (make !== undefined && make !== '') {
    dashView.make = make as 'make'
  }

  if (learn !== undefined && learn !== '') {
    dashView.learn = learn as 'learn'
  }

  if (tab !== undefined && tab !== '') {
    dashView.tab = tab as ClassTabs
  }

  // If a cross library filter exist, a mobile_page must be set
  // else the user will not be able to view the page in mobile directly
  if (dashView.mobilePage === undefined && dashView.filter !== undefined) {
    switch (dashView.filter) {
      case CrossLibraryToFilterMap.bookmarks:
      case CrossLibraryToFilterMap.imports:
        dashView.mobilePage = MobilePage.CollectionsMenu
        break
      case CrossLibraryToFilterMap.recents:
      case CrossLibraryToFilterMap.shared:
        dashView.mobilePage = MobilePage.Collection
        break
    }
  }

  // If a gallery param exist, a mobile_page must be set
  // else the user will not be able to view the page in mobile directly
  if (dashView.mobilePage === undefined && dashView.gallery !== undefined) {
    switch (dashView.gallery) {
      case 'all': // Special case for 'all' since it is used for the collections menu page
        dashView.mobilePage = MobilePage.CollectionsMenu
        break
      default:
        dashView.mobilePage = MobilePage.Collection
        break
    }
  }
  return dashView
}

function saveDashViewUrlIntoStorage(): void {
  safeLocalStorage.setItem('dashViewUrl', window.location.href)
}

function getDashViewUrlFromStorage(): string | null {
  return safeLocalStorage.getItem('dashViewUrl')
}

function clearDashViewUrlFromStorage(): void {
  safeLocalStorage.removeItem('dashViewUrl')
}

/**
 * Params that indicate explicit navigation intent and should not be overwritten by storage restoration.
 */
const EXPLICIT_DASH_PARAMS = {
  BASE: ['audience', 'folder_id', 'filter'] as const,
  CAMPUS: ['tab'] as const,
} as const

/**
 * Check if the current URL has explicit params that should be preserved (not overwritten by storage).
 * @param additionalParams - Additional param names to check (e.g., 'tab' for campus class pages)
 */
function hasExplicitDashParams(additionalParams: string[] = []): boolean {
  const currentUrlParams = new URLSearchParams(window.location.search)
  const paramsToCheck = [...EXPLICIT_DASH_PARAMS.BASE, ...additionalParams]
  return paramsToCheck.some((param) => currentUrlParams.has(param))
}

/**
 * Attempt to restore URL search params from localStorage.
 * @param additionalExplicitParams - Additional param names that indicate explicit navigation (e.g., 'tab')
 */
function tryRestoreUrlFromStorage(additionalExplicitParams: string[] = []): void {
  const dashViewUrlFromStorage = getDashViewUrlFromStorage()

  if (dashViewUrlFromStorage === null) return

  const currentUrl = new URL(window.location.href)
  const urlFromStorage = new URL(dashViewUrlFromStorage)

  // If pathname is not the same, don't restore (e.g., don't restore school page params on class page)
  if (urlFromStorage.pathname !== currentUrl.pathname) return

  // If the search params are already the same, nothing to do
  if (urlFromStorage.search === currentUrl.search) return

  // Don't restore if current URL already has explicit params that should be preserved
  if (hasExplicitDashParams(additionalExplicitParams)) return

  currentUrl.search = urlFromStorage.search
  window.history.pushState(null, '', currentUrl.toString())
  saveDashViewUrlIntoStorage()
}

export {
  dashViewUrlParams,
  dashViewUrl,
  getDashPathnameParams,
  getDashViewFromUrlParams,
  saveDashViewUrlIntoStorage,
  getDashViewUrlFromStorage,
  clearDashViewUrlFromStorage,
  EXPLICIT_DASH_PARAMS,
  hasExplicitDashParams,
  tryRestoreUrlFromStorage,
}
